import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Star, ClipboardPaste, CopyPlus } from 'lucide-react';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Tooltip from '../../components/ui/Tooltip';
import OptionsMenu from '../../components/ui/OptionsMenu';
import { SCALE, OPTION_TYPES, TYPES_WITH_MAX_SELECTIONS, QUESTION_TYPE_COLORS, typeLabelKey } from './questionEditorModel';

// Anket oluşturma sayfasının tek soru kartı (spec 2026-10-16 S1–S4). Puan sütunu YOK: ölçek sorusunun puanları sırayla 1–5,
// tek seçim puansız. "Olumsuz ifade" ve "Öne Çıkar" yalnız ölçek sorusunda. Kopyala/Yapıştır menüsü seçenek listesi
// düzenlenen tiplerde (OPTION_TYPES).

const QuestionCard = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  border-left: 4px solid ${({ $typeColor, theme }) => $typeColor || theme.colors.surface.border};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
`;

const Row = styled.div`
  display: flex;
  gap: ${({ $gap }) => $gap || '12px'};
  align-items: ${({ $align }) => $align || 'flex-start'};
  margin-bottom: ${({ $mb }) => $mb || '0'};
  flex-wrap: ${({ $wrap }) => $wrap || 'nowrap'};
`;

const QuestionNo = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Field = styled.div`
  margin-bottom: 12px;
`;

const Caption = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 8px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: -4px 0 10px;
`;

const FieldHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 6px;
`;

const OptionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: none;
  background: ${({ $active, theme }) => ($active ? theme.colors.auxiliar.warningSoft : 'none')};
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $danger, $active, theme }) => {
    if ($danger) return theme.colors.auxiliar.danger;
    return $active ? theme.colors.auxiliar.warningText : theme.colors.text.muted;
  }};
  &:hover {
    background: ${({ $danger, theme }) => ($danger ? theme.colors.auxiliar.dangerSoft : theme.colors.bgColor)};
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export function SectionSelect({ questions, value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const sections = useMemo(() => {
    const unique = new Set();
    questions.forEach(q => { if (q.sectionTitle) unique.add(q.sectionTitle); });
    return [...unique];
  }, [questions]);

  const filtered = useMemo(() => {
    if (!search) return sections;
    const q = search.toLocaleLowerCase('tr-TR');
    return sections.filter(s => s.toLocaleLowerCase('tr-TR').includes(q));
  }, [sections, search]);

  const showCreate = search && !sections.some(s => s.toLocaleLowerCase('tr-TR') === search.toLocaleLowerCase('tr-TR'));

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {(value || open) && (
        <div style={{ position: 'absolute', top: -8, left: 12, fontSize: 11, color: '#0291EF', background: '#fff', padding: '0 4px', zIndex: 1, fontFamily: "'Nunito Sans', sans-serif" }}>
          {t('survey.sectionTitle')}
        </div>
      )}
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px', border: `1.5px solid ${open ? '#0291EF' : '#E0E4E8'}`, borderRadius: 4,
          cursor: 'pointer', minHeight: 48, fontFamily: "'Nunito Sans', sans-serif", fontSize: 13,
          color: value ? '#004242' : '#A0A8B4', transition: 'border-color 0.2s',
        }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || t('survey.sectionTitlePlaceholder')}
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {value && (
            <span onClick={handleClear} style={{ cursor: 'pointer', color: '#A0A8B4', display: 'flex' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </span>
          )}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A0A8B4" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#fff', borderRadius: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          border: '1px solid #E8ECF0', marginTop: 4, maxHeight: 240, overflow: 'auto',
        }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('survey.sectionSearchOrCreate')}
            autoFocus
            style={{
              width: '100%', padding: '10px 12px', border: 'none', borderBottom: '1px solid #E8ECF0',
              fontFamily: "'Nunito Sans', sans-serif", fontSize: 13, outline: 'none', boxSizing: 'border-box',
            }}
          />
          {filtered.map(s => (
            <div
              key={s}
              onClick={() => handleSelect(s)}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontFamily: "'Nunito Sans', sans-serif",
                background: s === value ? '#eff6ff' : 'transparent', color: '#004242',
              }}
              onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
              onMouseLeave={(e) => e.target.style.background = s === value ? '#eff6ff' : 'transparent'}
            >
              {s}
            </div>
          ))}
          {showCreate && (
            <div
              onClick={() => handleSelect(search)}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontFamily: "'Nunito Sans', sans-serif",
                color: '#0291EF', fontWeight: 600, borderTop: filtered.length ? '1px solid #E8ECF0' : 'none',
              }}
              onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              + {t('survey.createSection')}: "{search}"
            </div>
          )}
          {!filtered.length && !showCreate && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: '#A0A8B4', fontFamily: "'Nunito Sans', sans-serif" }}>
              {t('survey.noSections')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function QuestionEditorCard({
  question, index, questions, clipboard, canUsePrevious, highlightLocked,
  onChange, onOptionLabelChange, onAddOption, onRemoveOption, onRemove,
  onToggleHighlight, onCopyOptions, onPasteOptions, onUsePreviousOptions,
}) {
  const { t } = useTranslation();
  const q = question;
  const isScale = q.type === SCALE;
  const hasOptionList = OPTION_TYPES.includes(q.type);
  const color = QUESTION_TYPE_COLORS[q.type];

  const menuActions = hasOptionList
    ? [
      { type: 'copy', label: t('survey.copyOptions'), onClick: () => onCopyOptions() },
      {
        type: 'paste',
        icon: ClipboardPaste,
        label: t('survey.pasteOptions'),
        onClick: () => onPasteOptions(),
        condition: () => Array.isArray(clipboard) && clipboard.length > 0,
      },
    ]
    : [];

  return (
    <QuestionCard $typeColor={color}>
      <Row $align="center" $mb="12px">
        <QuestionNo>{index + 1}.</QuestionNo>
        <TypeBadge $color={color}>{t(typeLabelKey(q.type))}</TypeBadge>
        <div style={{ flex: 1 }} />
        {isScale && (
          // İpucu uzun ve Tooltip tek satır (nowrap) çizer; düğme kartın sağ ucunda — sola açılır ki ekran dışına taşmasın.
          <Tooltip text={t('survey.highlightHint')} position="left">
            <IconButton
              type="button"
              data-testid="highlight-toggle"
              aria-pressed={!!q.isHeadline}
              $active={!!q.isHeadline}
              disabled={!q.isHeadline && highlightLocked}
              onClick={onToggleHighlight}
            >
              <Star size={15} fill={q.isHeadline ? 'currentColor' : 'none'} />
              <span>{t('survey.highlight')}</span>
            </IconButton>
          </Tooltip>
        )}
        <CheckboxInput label={t('survey.isRequired')} checked={q.isRequired} onChange={(v) => onChange('isRequired', v)} />
        {menuActions.length > 0 && <OptionsMenu actions={menuActions} row={q} />}
        <IconButton type="button" $danger aria-label={t('survey.deleteQuestion')} onClick={onRemove}>
          <Trash2 size={16} />
        </IconButton>
      </Row>

      <Field>
        <TextInput label={t('survey.questionText')} value={q.text} onChange={(e) => onChange('text', e.target.value)} />
      </Field>

      <Field>
        <SectionSelect questions={questions} value={q.sectionTitle || ''} onChange={(v) => onChange('sectionTitle', v)} />
        {/* Son inceleme I2 — bölümsüz ölçek sorusu raporda başlık ve grup karşılaştırması üretmez; uzman olmayan İK'ya ipucu. */}
        {isScale && !String(q.sectionTitle || '').trim() && (
          <FieldHint data-testid="section-hint">{t('survey.sectionHint')}</FieldHint>
        )}
      </Field>

      {isScale && (
        <div>
          <Caption>{t('survey.options')}</Caption>
          <Hint>{t('survey.scaleHint')}</Hint>
          {q.options.map((opt) => (
            <OptionRow key={opt.id} data-testid="option-row">
              <div style={{ flex: 1 }}>
                <TextInput
                  label={t('survey.optionLabel')}
                  value={opt.label}
                  onChange={(e) => onOptionLabelChange(opt.id, e.target.value)}
                />
              </div>
            </OptionRow>
          ))}
          <div data-testid="negative-statement" style={{ marginTop: 8 }}>
            <CheckboxInput
              label={t('survey.negativeStatement')}
              checked={!!q.isReverseScored}
              onChange={(v) => onChange('isReverseScored', v)}
            />
          </div>
        </div>
      )}

      {hasOptionList && (
        <div>
          <Caption>{t('survey.options')}</Caption>
          {q.options.map((opt) => (
            <OptionRow key={opt.id} data-testid="option-row">
              <div style={{ flex: 1 }}>
                <TextInput
                  label={t('survey.optionLabel')}
                  value={opt.label}
                  onChange={(e) => onOptionLabelChange(opt.id, e.target.value)}
                />
              </div>
              <IconButton type="button" $danger aria-label={t('common.delete')} onClick={() => onRemoveOption(opt.id)}>
                <Trash2 size={14} />
              </IconButton>
            </OptionRow>
          ))}
          <Row $align="center" $gap="12px" $wrap="wrap">
            <Button variant="ghost" onClick={onAddOption} style={{ fontSize: 13, padding: '6px 12px' }}>
              <Plus size={14} /> {t('survey.addOption')}
            </Button>
            {canUsePrevious && (
              <Button variant="ghost" data-testid="use-previous-options" onClick={onUsePreviousOptions}
                style={{ fontSize: 13, padding: '6px 12px' }}>
                <CopyPlus size={14} /> {t('survey.usePreviousOptions')}
              </Button>
            )}
            {TYPES_WITH_MAX_SELECTIONS.includes(q.type) && (
              <div style={{ width: 160 }}>
                <TextInput
                  label={t('survey.maxSelections')}
                  value={q.maxSelections || ''}
                  type="number"
                  placeholder={t('survey.noLimit')}
                  onChange={(e) => onChange('maxSelections', e.target.value ? Number(e.target.value) : null)}
                />
              </div>
            )}
          </Row>
        </div>
      )}
    </QuestionCard>
  );
}
